// src/App.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  LayersControl,
  Polyline,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet marker path
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// ---------- Utilities ----------
function toKey(lat, lon) {
  return `${lat.toFixed(6)},${lon.toFixed(6)}`;
}
function fromKey(key) {
  const [lat, lon] = key.split(",").map((s) => parseFloat(s));
  return [lat, lon];
}
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // km
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

class MinHeap {
  constructor() {
    this.heap = [];
  }
  push(item) {
    this.heap.push(item);
    this._siftUp();
  }
  pop() {
    if (this.heap.length === 0) return null;
    const top = this.heap[0];
    const last = this.heap.pop();
    if (this.heap.length > 0) {
      this.heap[0] = last;
      this._siftDown();
    }
    return top;
  }
  _siftUp() {
    let i = this.heap.length - 1;
    while (i > 0) {
      let p = Math.floor((i - 1) / 2);
      if (this.heap[p].priority <= this.heap[i].priority) break;
      [this.heap[p], this.heap[i]] = [this.heap[i], this.heap[p]];
      i = p;
    }
  }
  _siftDown() {
    let i = 0;
    const n = this.heap.length;
    while (true) {
      let l = 2 * i + 1;
      let r = 2 * i + 2;
      let smallest = i;
      if (l < n && this.heap[l].priority < this.heap[smallest].priority)
        smallest = l;
      if (r < n && this.heap[r].priority < this.heap[smallest].priority)
        smallest = r;
      if (smallest === i) break;
      [this.heap[i], this.heap[smallest]] = [this.heap[smallest], this.heap[i]];
      i = smallest;
    }
  }
  isEmpty() {
    return this.heap.length === 0;
  }
}

// ---------- Overpass / Nominatim helpers ----------
async function fetchStationsInBounds(bounds) {
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();
  const query = `
    [out:json][timeout:25];
    node["railway"="station"](${sw.lat},${sw.lng},${ne.lat},${ne.lng});
    out body;
  `;
  const res = await fetch("https://overpass-api.de/api/interpreter", {
    method: "POST",
    body: query,
  });
  const data = await res.json();
  return (data.elements || []).map((el) => ({
    id: el.id,
    name: (el.tags && el.tags.name) || "Unnamed station",
    coords: [el.lat, el.lon],
  }));
}

async function fetchRailwayWaysForBBox(minLat, minLon, maxLat, maxLon) {
  const query = `
    [out:json][timeout:60];
    way["railway"~"^(rail|light_rail|subway|tram)$"](${minLat},${minLon},${maxLat},${maxLon});
    (._;>;);
    out geom;
  `;
  const res = await fetch("https://overpass-api.de/api/interpreter", {
    method: "POST",
    body: query,
  });
  const data = await res.json();
  return (data.elements || []).filter((el) => el.type === "way" && el.geometry);
}

function buildGraphFromWays(ways) {
  const adjacency = new Map();
  const nodesSet = new Set();

  function addEdge(aKey, bKey, dist) {
    if (!adjacency.has(aKey)) adjacency.set(aKey, []);
    adjacency.get(aKey).push({ to: bKey, weight: dist });
  }

  ways.forEach((way) => {
    const pts = way.geometry;
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      const aKey = toKey(a.lat, a.lon);
      const bKey = toKey(b.lat, b.lon);
      nodesSet.add(aKey);
      nodesSet.add(bKey);
      const d = haversineDistance(a.lat, a.lon, b.lat, b.lon);
      addEdge(aKey, bKey, d);
      addEdge(bKey, aKey, d);
    }
  });

  return { adjacency, nodesSet };
}

function findNearestNodeKey(lat, lon, nodesSet) {
  let best = null;
  let bestDist = Infinity;
  for (const key of nodesSet) {
    const [nlat, nlon] = fromKey(key);
    const d = haversineDistance(lat, lon, nlat, nlon);
    if (d < bestDist) {
      bestDist = d;
      best = key;
    }
  }
  return { key: best, distanceKm: bestDist };
}

function dijkstra(adjacency, startKey, endKey) {
  const dist = new Map();
  const prev = new Map();
  const heap = new MinHeap();

  dist.set(startKey, 0);
  heap.push({ node: startKey, priority: 0 });

  while (!heap.isEmpty()) {
    const cur = heap.pop();
    const u = cur.node;
    const dU = cur.priority;
    if (dU > (dist.get(u) ?? Infinity)) continue;
    if (u === endKey) break;

    const neighbors = adjacency.get(u) || [];
    for (const nb of neighbors) {
      const v = nb.to;
      const alt = dU + nb.weight;
      if (alt < (dist.get(v) ?? Infinity)) {
        dist.set(v, alt);
        prev.set(v, u);
        heap.push({ node: v, priority: alt });
      }
    }
  }

  if (!dist.has(endKey)) return null;

  const path = [];
  let cur = endKey;
  while (cur) {
    path.push(cur);
    if (cur === startKey) break;
    cur = prev.get(cur);
  }
  path.reverse();
  return { path, distanceKm: dist.get(endKey) };
}

// ---------- Map components ----------
function StationLoader({ setStations }) {
  const map = useMapEvents({
    moveend: () => maybeFetchStations(map, setStations),
  });

  useEffect(() => {
    maybeFetchStations(map, setStations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  return null;
}

async function maybeFetchStations(map, setStations) {
  const bounds = map.getBounds();
  const sw = bounds.getSouthWest();
  const ne = bounds.getNorthEast();

  const widthKm = haversineDistance(sw.lat, sw.lng, sw.lat, ne.lng);
  const heightKm = haversineDistance(sw.lat, sw.lng, ne.lat, sw.lng);

  if (widthKm <= 10 && heightKm <= 10) {
    setStations([]); // quick clear to show loading
    try {
      const s = await fetchStationsInBounds(bounds);
      setStations(s);
    } catch (err) {
      console.error("station fetch failed", err);
      setStations([]);
    }
  } else {
    setStations([]);
  }
}

// Use Leaflet popup directly for reliability
function FlyToAndPopup({ station }) {
  const map = useMap();
  useEffect(() => {
    if (station) {
      map.setView(station.coords, 16);
      // small timeout to ensure map has moved
      setTimeout(() => {
        L.popup({ maxWidth: 350 })
          .setLatLng(station.coords)
          .setContent(`<div style="font-weight:600">${station.name}</div>`)
          .openOn(map);
      }, 250);
    }
  }, [station, map]);
  return null;
}

// ---------- Nominatim search (global station search) ----------
async function searchStationsNominatim(query) {
  if (!query || query.trim().length === 0) return [];
  // Append "station" to bias results to stations
  const q = `${query.trim()} station`;
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
    q
  )}&limit=12&addressdetails=0`;
  const res = await fetch(url, {
    headers: { "Accept-Language": "en" },
  });
  const data = await res.json();
  // Filter reasonably for railway/station-like results and map to our shape
  const filtered = data
    .filter(
      (it) =>
        it &&
        (it.class === "railway" ||
          (it.type && it.type.toLowerCase().includes("station")) ||
          (it.display_name && it.display_name.toLowerCase().includes("station")))
    )
    .map((it) => ({
      id: `${it.osm_type}-${it.osm_id}`,
      name: (it.display_name || "").split(",")[0] || it.display_name,
      coords: [parseFloat(it.lat), parseFloat(it.lon)],
    }));
  return filtered;
}

// ---------- Main App ----------
export default function App() {
  const [stations, setStations] = useState([]); // local stations fetched for visible bbox
  const [startStation, setStartStation] = useState(null);
  const [routeCoords, setRouteCoords] = useState([]);
  const [trackDistanceKm, setTrackDistanceKm] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [remoteResults, setRemoteResults] = useState([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const lastSearchRef = useRef("");
  const [flyToStation, setFlyToStation] = useState(null);

  const mapCenter = [51.1657, 10.4515];
  const lastFetchedRef = useRef(0);

  // Dijkstra / route functions (same as before)
  const onStationClick = async (station) => {
    setStatusMessage(null);
    setRouteCoords([]);
    setTrackDistanceKm(null);

    if (!startStation) {
      setStartStation(station);
      setStatusMessage("Starting station selected. Choose destination.");
      return;
    }

    if (startStation.id === station.id) {
      setStartStation(null);
      setStatusMessage(null);
      return;
    }

    setLoadingRoute(true);
    setStatusMessage("Fetching railway geometry and computing shortest path...");

    try {
      const pad = 0.02;
      const minLat = Math.min(startStation.coords[0], station.coords[0]) - pad;
      const maxLat = Math.max(startStation.coords[0], station.coords[0]) + pad;
      const minLon = Math.min(startStation.coords[1], station.coords[1]) - pad;
      const maxLon = Math.max(startStation.coords[1], station.coords[1]) + pad;

      const now = Date.now();
      if (now - lastFetchedRef.current < 800) {
        await new Promise((r) => setTimeout(r, 300));
      }

      const ways = await fetchRailwayWaysForBBox(minLat, minLon, maxLat, maxLon);
      lastFetchedRef.current = Date.now();

      if (!ways || ways.length === 0) {
        setStatusMessage("No railway geometry found in area.");
        setStartStation(null);
        setLoadingRoute(false);
        return;
      }

      const { adjacency, nodesSet } = buildGraphFromWays(ways);

      const startNearest = findNearestNodeKey(
        startStation.coords[0],
        startStation.coords[1],
        nodesSet
      );
      const endNearest = findNearestNodeKey(
        station.coords[0],
        station.coords[1],
        nodesSet
      );

      if (!startNearest.key || !endNearest.key) {
        setStatusMessage("Start or end station not connected to fetched rail graph.");
        setStartStation(null);
        setLoadingRoute(false);
        return;
      }

      if (startNearest.distanceKm > 2 || endNearest.distanceKm > 2) {
        setStatusMessage(
          "Nearest track node to a station is >2km away. Try zooming in more or enlarge padding."
        );
      }

      const dres = dijkstra(adjacency, startNearest.key, endNearest.key);
      if (!dres) {
        setStatusMessage("No rail path found between these stations (within fetched area).");
        setStartStation(null);
        setLoadingRoute(false);
        return;
      }

      const coords = dres.path.map(fromKey);
      setRouteCoords(coords);
      setTrackDistanceKm(dres.distanceKm.toFixed(2));
      setStatusMessage("Route computed.");
    } catch (err) {
      console.error("error computing route", err);
      setStatusMessage("Error computing route. See console for details.");
    } finally {
      setStartStation(null);
      setLoadingRoute(false);
    }
  };

  // Filter local stations matching the query
  const localMatches = stations
    .filter((s) => s.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .slice(0, 6);

  // Debounced remote search (Nominatim)
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length === 0) {
      setRemoteResults([]);
      setRemoteLoading(false);
      lastSearchRef.current = "";
      return;
    }

    const timer = setTimeout(() => {
      lastSearchRef.current = q;
      setRemoteLoading(true);
      searchStationsNominatim(q)
        .then((res) => {
          // only apply results if query didn't change in the meantime
          if (lastSearchRef.current === q) {
            // dedupe remote results by coords/name
            setRemoteResults(res.slice(0, 8));
          }
        })
        .catch((err) => {
          console.error("remote search failed", err);
          if (lastSearchRef.current === q) setRemoteResults([]);
        })
        .finally(() => {
          if (lastSearchRef.current === q) setRemoteLoading(false);
        });
    }, 350);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Combine local + remote suggestions (dedupe by name+coords)
  const combinedSuggestions = (() => {
    const mapId = new Map();
    [...localMatches, ...remoteResults].forEach((s) => {
      const key = `${s.name}|${s.coords[0].toFixed(6)},${s.coords[1].toFixed(6)}`;
      if (!mapId.has(key)) mapId.set(key, s);
    });
    return Array.from(mapId.values()).slice(0, 10);
  })();

  return (
    <div style={{ height: "100vh", width: "100%", position: "relative" }}>
      <MapContainer
        center={mapCenter}
        zoom={6}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
      >
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="Minimal (Country Borders)">
            <TileLayer
              attribution='&copy; OpenMapTiles, OpenStreetMap'
              url="https://basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
            />
          </LayersControl.BaseLayer>

          <LayersControl.Overlay checked name="Train Lines (OpenRailwayMap)">
            <TileLayer
              attribution='&copy; OpenRailwayMap contributors'
              url="https://tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png"
              opacity={0.8}
            />
          </LayersControl.Overlay>
        </LayersControl>

        <StationLoader setStations={setStations} />

        {/* Station markers */}
        {stations.map((st) => (
          <Marker key={st.id} position={st.coords}>
            <Popup>
              <div style={{ minWidth: 200 }}>
                <div style={{ fontWeight: 600 }}>{st.name}</div>
                <div style={{ marginTop: 6 }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onStationClick(st);
                    }}
                    style={{
                      padding: "6px 10px",
                      cursor: "pointer",
                      background: startStation?.id === st.id ? "#28a745" : "#007bff",
                      color: "white",
                      border: "none",
                      borderRadius: 4,
                    }}
                  >
                    {startStation?.id === st.id
                      ? "Starting point selected"
                      : startStation
                      ? "Finish route to here"
                      : "Select as start"}
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Resulting shortest track path */}
        {routeCoords.length > 0 && <Polyline positions={routeCoords} color="red" weight={5} />}

        {/* Fly and show popup for search-selected station */}
        <FlyToAndPopup station={flyToStation} />
      </MapContainer>

      {/* HUD / messages */}
      <div
        style={{
          position: "absolute",
          top: 14,
          left: 14,
          minWidth: 220,
          background: "rgba(255,255,255,0.95)",
          padding: 10,
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          fontSize: 14,
          zIndex: 1000,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Rail Route Tool</div>
        <div style={{ marginBottom: 6 }}>
          Zoom in until map area is ≤ 10×10 km to see stations.
        </div>
        <div style={{ marginBottom: 6 }}>
          {startStation ? (
            <div>
              Selected start: <b>{startStation.name}</b>
            </div>
          ) : (
            <div>Select a station to start.</div>
          )}
        </div>

        <div style={{ color: "gray", marginBottom: 6 }}>
          {loadingRoute ? "Computing route..." : statusMessage}
        </div>

        {trackDistanceKm && (
          <div style={{ marginTop: 6 }}>
            <b>Track distance:</b> {trackDistanceKm} km
          </div>
        )}
      </div>

      {/* Search Button */}
      <button
        onClick={() => {
          setSearchOpen(true);
          setSearchQuery("");
          setRemoteResults([]);
        }}
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          zIndex: 1000,
          padding: "8px 12px",
          background: "#007bff",
          color: "white",
          border: "none",
          borderRadius: 6,
          cursor: "pointer",
        }}
      >
        Search Station
      </button>

      {/* Search Overlay */}
      {searchOpen && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "white",
              padding: 16,
              borderRadius: 8,
              width: 360,
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>Search Station</h3>
              <button
                onClick={() => {
                  setSearchOpen(false);
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <input
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Type station name..."
              style={{
                width: "100%",
                padding: 8,
                marginTop: 10,
                boxSizing: "border-box",
              }}
            />

            <div style={{ marginTop: 10, maxHeight: 300, overflowY: "auto" }}>
              {remoteLoading && <div style={{ color: "#666", marginBottom: 6 }}>Searching...</div>}
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {combinedSuggestions.map((s) => (
                  <li
                    key={s.id || `${s.name}-${s.coords[0]}-${s.coords[1]}`}
                    onClick={() => {
                      setFlyToStation(s);
                      setSearchOpen(false);
                      setSearchQuery("");
                    }}
                    style={{
                      padding: "8px 6px",
                      borderBottom: "1px solid #eee",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {s.coords[0].toFixed(5)}, {s.coords[1].toFixed(5)}
                    </div>
                  </li>
                ))}
                {combinedSuggestions.length === 0 && (
                  <li style={{ padding: 8, color: "#777" }}>
                    {remoteLoading ? "Searching..." : "No matches"}
                  </li>
                )}
              </ul>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button
                onClick={() => setSearchOpen(false)}
                style={{
                  padding: "8px 12px",
                  background: "#ddd",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
